import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import { dashboardService } from '../../services/dashboard';
import { setViewMode, setSelectedPet, setSelectedIssueType, setSelectedTimestamp, setPlaybackData, setPlaybackLoading, setPlaybackPlaying, setPlaybackProgress, addHeartRateData, addBreathRateData, addThermalData, addDogPose, addImage, clearHeartRateBuffer, clearBreathRateBuffer, clearThermalBuffer, clearDogPoses, clearImages, togglePanel, setVisiblePanels, ALL_PANELS, PANEL_LABELS, type DashboardPanelId } from '../../store/slices/dashboardSlice';
import { ISSUE_TYPES, ISSUE_TYPE_PANEL_MAP, ISSUE_TYPE_VIDEO_MAP } from '../../services/playbackUrl';
import HeartRatePanel from './HeartRatePanel';
import BreathRatePanel from './BreathRatePanel';
import ObjectDetectionPanel from './ObjectDetectionPanel';
import ThermalPanel from './ThermalPanel';
import ImageViewerPanel from './ImageViewerPanel';


interface DashboardViewProps {
  onNavigateHome?: () => void;
}

const DashboardView: React.FC<DashboardViewProps> = ({ onNavigateHome }) => {
  const { isConnected, cameraName, isSessionActive, viewMode, selectedPet, selectedIssueType, selectedTimestamp, isPlaybackLoading, isPlaybackPlaying, playbackProgress, visiblePanels } = useSelector((state: RootState) => state.dashboard);
  const dispatch = useDispatch();
  const [subjectName, setSubjectName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasAutoplayed, setHasAutoplayed] = useState(false);
  const [devices, setDevices] = useState<string[]>([]);
  const [playbackSpeed, setPlaybackSpeed] = useState<1 | 2 | 5 | 10>(1);
  const playbackTimerRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
  const playbackSpeedRef = React.useRef(playbackSpeed);
  playbackSpeedRef.current = playbackSpeed;

  useEffect(() => {
    dashboardService.init();
    dashboardService.connect().catch((err) => {
      console.error('[DashboardView] Auto-connect failed:', err);
    });
    dashboardService.fetchDevices().then((names) => {
      setDevices(names);
    }).catch(() => {});

    // NOTE: Do NOT call dashboardService.stopSession() on unmount.
    // The session should persist across navigation (Dashboard ↔ Mobile).
    // The user must explicitly stop the session via the Stop button.
    return () => {
      if (playbackTimerRef.current) {
        clearInterval(playbackTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (viewMode === 'playback' && selectedPet && selectedIssueType && selectedTimestamp && !hasAutoplayed) {
      setHasAutoplayed(true);
      startPlayback();
    }
  }, [viewMode, selectedPet, selectedIssueType, selectedTimestamp, hasAutoplayed]);

  useEffect(() => {
    if (viewMode !== 'playback') {
      setHasAutoplayed(false);
      if (playbackTimerRef.current) {
        clearInterval(playbackTimerRef.current);
        playbackTimerRef.current = null;
      }
      dispatch(setPlaybackPlaying(false));
      dispatch(setPlaybackProgress(0));
    }
  }, [viewMode]);

  const startPlayback = async () => {
    dispatch(setPlaybackLoading(true));
    dispatch(clearHeartRateBuffer());
    dispatch(clearBreathRateBuffer());
    dispatch(clearThermalBuffer());
    dispatch(clearDogPoses());
    dispatch(clearImages());
    dispatch(setPlaybackProgress(0));

    try {
      const timestamp = new Date(selectedTimestamp).getTime();
      const from = timestamp - 600000;
      const to = timestamp + 180000;
      const data = await dashboardService.fetchPlayback(selectedPet, selectedIssueType, String(from), String(to));

      if (!data || (data.heartRate.length === 0 && data.breathRate.length === 0 && data.thermal.length === 0 && data.images.length === 0 && data.dogPoses.length === 0)) {
        dispatch(setPlaybackData(data));
        dispatch(setPlaybackLoading(false));
        return;
      }

      dispatch(setPlaybackData(data));
      dispatch(setPlaybackLoading(false));

      const allPoints: Array<{ timestamp: number; type: 'heartRate' | 'breathRate' | 'thermal' | 'dogPose' | 'image'; data: any }> = [
        ...data.heartRate.map(d => ({ timestamp: d.timestamp, type: 'heartRate' as const, data: d })),
        ...data.breathRate.map(d => ({ timestamp: d.timestamp, type: 'breathRate' as const, data: d })),
        ...data.thermal.map(d => ({ timestamp: d.timestamp, type: 'thermal' as const, data: d })),
        ...data.dogPoses.map(d => ({ timestamp: d.timestamp, type: 'dogPose' as const, data: d })),
        ...data.images.map(d => ({ timestamp: d.timestamp, type: 'image' as const, data: d })),
      ].sort((a, b) => a.timestamp - b.timestamp);

      if (allPoints.length === 0) return;

      const minTs = allPoints[0].timestamp;
      const maxTs = allPoints[allPoints.length - 1].timestamp;
      const duration = maxTs - minTs || 1;

      const PLAYBACK_INTERVAL_MS = 100;
      let currentVirtualTime = minTs;
      let idx = 0;

      dispatch(setPlaybackPlaying(true));

      playbackTimerRef.current = setInterval(() => {
        currentVirtualTime += PLAYBACK_INTERVAL_MS * playbackSpeedRef.current;

        while (idx < allPoints.length && allPoints[idx].timestamp <= currentVirtualTime) {
          const point = allPoints[idx];
          switch (point.type) {
            case 'heartRate':
              dispatch(addHeartRateData(point.data as any));
              break;
            case 'breathRate':
              dispatch(addBreathRateData(point.data as any));
              break;
            case 'thermal':
              dispatch(addThermalData(point.data as any));
              break;
            case 'dogPose':
              dispatch(addDogPose(point.data as any));
              break;
            case 'image':
              dispatch(addImage(point.data));
              break;
          }
          idx++;
        }

        dispatch(setPlaybackProgress(Math.min(100, ((currentVirtualTime - minTs) / duration) * 100)));

        if (idx >= allPoints.length) {
          if (playbackTimerRef.current) {
            clearInterval(playbackTimerRef.current);
            playbackTimerRef.current = null;
          }
          dispatch(setPlaybackPlaying(false));
          dispatch(setPlaybackProgress(100));
        }
      }, PLAYBACK_INTERVAL_MS);

    } catch (err) {
      console.error('[DashboardView] Playback load failed:', err);
      dispatch(setPlaybackData(null));
      dispatch(setPlaybackLoading(false));
    }
  };

  const stopPlayback = () => {
    if (playbackTimerRef.current) {
      clearInterval(playbackTimerRef.current);
      playbackTimerRef.current = null;
    }
    dispatch(setPlaybackPlaying(false));
  };

  const PANEL_COMPONENTS: Record<DashboardPanelId, React.ComponentType<any>> = {
    heartRate: HeartRatePanel,
    breathRate: BreathRatePanel,
    thermal: ThermalPanel,
    petDetection: ObjectDetectionPanel,
    images: ImageViewerPanel,
  };

  const activePanels = ALL_PANELS.filter(p => visiblePanels.includes(p));

  const handleStartSession = async () => {
    setIsLoading(true);
    try {
      await dashboardService.startSession(subjectName || undefined);
    } catch (err) {
      console.error('[DashboardView] Failed to start session:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStopSession = async () => {
    setIsLoading(true);
    try {
      await dashboardService.stopSession();
    } catch (err) {
      console.error('[DashboardView] Failed to stop session:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col dark:bg-claude-darkBg bg-claude-bg h-full">
      <div className="draggable flex h-12 items-center justify-between px-4 border-b dark:border-claude-darkBorder border-claude-border shrink-0">
        <div className="non-draggable flex items-center gap-3">
          <button
            onClick={onNavigateHome}
            className="h-8 px-3 inline-flex items-center gap-1.5 rounded-lg dark:text-claude-darkTextSecondary text-claude-textSecondary hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover transition-colors text-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <h1 className="text-lg font-semibold text-gray-900 dark:text-claude-darkText">
            Health Dashboard
          </h1>
          {isSessionActive && (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></span>
              Recording
            </span>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0 p-4">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Mode Switch & Controls */}
          <div className="bg-white dark:bg-claude-darkSurface rounded-xl shadow-sm border border-gray-200 dark:border-claude-darkBorder">
            {/* Mode Tabs */}
            <div className="flex border-b border-gray-200 dark:border-claude-darkBorder">
              <button
                onClick={() => { dispatch(setViewMode('realtime')); dispatch(setPlaybackData(null)); }}
                className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors relative ${
                  viewMode === 'realtime'
                    ? 'text-blue-600 dark:text-blue-400'
                    : 'text-gray-500 dark:text-claude-darkTextSecondary hover:text-gray-700 dark:hover:text-claude-darkText'
                }`}
              >
                <span className="flex items-center justify-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Realtime
                </span>
                {viewMode === 'realtime' && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400" />
                )}
              </button>
              <button
                onClick={() => dispatch(setViewMode('playback'))}
                className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors relative ${
                  viewMode === 'playback'
                    ? 'text-blue-600 dark:text-blue-400'
                    : 'text-gray-500 dark:text-claude-darkTextSecondary hover:text-gray-700 dark:hover:text-claude-darkText'
                }`}
              >
                <span className="flex items-center justify-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Playback
                </span>
                {viewMode === 'playback' && (
                  <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400" />
                )}
              </button>
            </div>

            {/* Mode Content */}
            <div className="p-4">
              {viewMode === 'realtime' ? (
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 dark:text-claude-darkText mb-1">
                      Subject Name (optional)
                    </label>
                    <input
                      type="text"
                      value={subjectName}
                      onChange={(e) => setSubjectName(e.target.value)}
                      placeholder="e.g., dog-fluffy or leave empty for auto-generated"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-claude-darkBorder rounded-lg text-sm dark:bg-claude-darkBg dark:text-claude-darkText focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={isSessionActive || isLoading}
                    />
                  </div>
                  <div className="flex items-center gap-2 mt-5">
                    {isSessionActive ? (
                      <button
                        onClick={handleStopSession}
                        disabled={isLoading}
                        className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {isLoading ? (
                          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <rect x="6" y="6" width="12" height="12" />
                          </svg>
                        )}
                        Stop Session
                      </button>
                    ) : (
                      <button
                        onClick={handleStartSession}
                        disabled={isLoading || !isConnected}
                        className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {isLoading ? (
                          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        )}
                        Start Session
                      </button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-claude-darkText mb-1">
                        Device
                      </label>
                      <select
                        value={selectedPet}
                        onChange={(e) => dispatch(setSelectedPet(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-claude-darkBorder rounded-lg text-sm dark:bg-claude-darkBg dark:text-claude-darkText focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select Device</option>
                        {devices.length > 0 ? (
                          devices.map((name) => (
                            <option key={name} value={name}>{name}</option>
                          ))
                        ) : (
                          <option value="" disabled>No devices found - enter manually below</option>
                        )}
                      </select>
                      {devices.length === 0 && (
                        <input
                          type="text"
                          value={selectedPet}
                          onChange={(e) => dispatch(setSelectedPet(e.target.value))}
                          placeholder="Enter device name"
                          className="mt-1 w-full px-3 py-1.5 border border-gray-300 dark:border-claude-darkBorder rounded-lg text-xs dark:bg-claude-darkBg dark:text-claude-darkText focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      )}
                    </div>
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-claude-darkText mb-1">
                        Issue Type
                      </label>
                      <select
                        value={selectedIssueType}
                        onChange={(e) => {
                          const issueType = e.target.value;
                          dispatch(setSelectedIssueType(issueType));
                          if (issueType && ISSUE_TYPE_PANEL_MAP[issueType]) {
                            dispatch(setVisiblePanels(ISSUE_TYPE_PANEL_MAP[issueType]));
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-claude-darkBorder rounded-lg text-sm dark:bg-claude-darkBg dark:text-claude-darkText focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select Issue</option>
                        {ISSUE_TYPES.map((issue) => (
                          <option key={issue.id} value={issue.id}>{issue.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-claude-darkText mb-1">
                        Timestamp
                      </label>
                      <input
                        type="datetime-local"
                        value={selectedTimestamp ? selectedTimestamp.slice(0, 16) : ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val) {
                            dispatch(setSelectedTimestamp(new Date(val).toISOString()));
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-claude-darkBorder rounded-lg text-sm dark:bg-claude-darkBg dark:text-claude-darkText focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-gray-500 dark:text-claude-darkTextSecondary">
                      {isPlaybackPlaying ? `Playing back... ${Math.round(playbackProgress)}%` : 'Select a device, issue type, and timestamp to view historical health data.'}
                    </p>
                    <div className="flex items-center gap-2">
                      <select
                        value={playbackSpeed}
                        onChange={(e) => setPlaybackSpeed(Number(e.target.value) as 1 | 2 | 5 | 10)}
                        className="px-2 py-1.5 border border-gray-300 dark:border-claude-darkBorder rounded-lg text-sm dark:bg-claude-darkBg dark:text-claude-darkText focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value={1}>1x</option>
                        <option value={2}>2x</option>
                        <option value={5}>5x</option>
                        <option value={10}>10x</option>
                      </select>
                      {isPlaybackPlaying && (
                        <button
                          onClick={stopPlayback}
                          className="px-3 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium transition-colors flex items-center gap-1.5"
                        >
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                            <rect x="6" y="6" width="12" height="12" />
                          </svg>
                          Stop
                        </button>
                      )}
                      <button
                        onClick={startPlayback}
                        disabled={isPlaybackLoading || isPlaybackPlaying || !selectedPet || !selectedIssueType || !selectedTimestamp}
                        className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {isPlaybackLoading ? (
                          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        )}
                        {isPlaybackPlaying ? 'Playing...' : 'Play'}
                      </button>
                    </div>
                  </div>
                  {playbackProgress > 0 && playbackProgress < 100 && (
                    <div className="mt-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 overflow-hidden">
                      <div
                        className="h-full bg-blue-500 rounded-full transition-all duration-300"
                        style={{ width: `${playbackProgress}%` }}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-medium text-gray-700 dark:text-claude-darkText">Panels</h2>
            <div className="flex flex-wrap gap-1.5">
              {ALL_PANELS.map((panel) => {
                const isVisible = visiblePanels.includes(panel);
                return (
                  <button
                    key={panel}
                    onClick={() => dispatch(togglePanel(panel))}
                    className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors border ${
                      isVisible
                        ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-600 dark:text-blue-400'
                        : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    {PANEL_LABELS[panel]}
                  </button>
                );
              })}
            </div>
          </div>

          {activePanels.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {activePanels.map((panelId) => {
                const Panel = PANEL_COMPONENTS[panelId];
                const videoUrl = ISSUE_TYPE_VIDEO_MAP[selectedIssueType]?.[panelId];
                const isPlayback = viewMode === 'playback';
                return Panel ? <Panel key={panelId} videoUrl={videoUrl} autoPlay={isPlayback && !!videoUrl} /> : null;
              })}
            </div>
          ) : (
            <div className="flex items-center justify-center h-32 text-gray-400 dark:text-claude-darkTextSecondary text-sm">
              No panels selected. Toggle panels above to view data.
            </div>
          )}

          {/* Connection Status - at the bottom */}
          <div className="bg-white dark:bg-claude-darkSurface rounded-xl p-4 shadow-sm border border-gray-200 dark:border-claude-darkBorder">
            {isConnected ? (
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-500 dark:text-claude-darkTextSecondary">
                  Connected to server: <span className="font-medium text-gray-700 dark:text-claude-darkText">{cameraName}</span>
                  <br />
                  <span className="text-xs">Device: {dashboardService.getCurrentDeviceName()}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-green-500">Connected</span>
                  <div className="w-2 h-2 rounded-full bg-green-500"></div>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse"></div>
                <span className="text-sm text-gray-500 dark:text-claude-darkTextSecondary">
                  Connecting to server...
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardView;